import { useState, useEffect, useCallback, useMemo } from 'react';
import { X, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, Sparkles, Save, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { bankCategoryRulesService } from '../../lib/bankCategoryRulesService';
import type { AccountingCategory } from '../../types/database';

interface ReviewTransaction {
  id: string;
  description: string;
  amount: number;
  transaction_date: string;
  accounting_category_id: string | null;
  bank_accounts?: { nickname: string } | null;
  accounting_categories?: { name: string } | null;
}

interface ImportReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: string;
  transactionIds: string[];
  onComplete: () => void;
}

const fmtAmount = (n: number) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(n);

const fmtDate = (d: string) => {
  try {
    return new Date(d).toLocaleDateString('he-IL');
  } catch {
    return d;
  }
};

export default function ImportReviewModal({
  isOpen,
  onClose,
  companyId,
  transactionIds,
  onComplete,
}: ImportReviewModalProps) {
  const [transactions, setTransactions] = useState<ReviewTransaction[]>([]);
  const [categories, setCategories] = useState<AccountingCategory[]>([]);
  const [edits, setEdits] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSummary, setSavedSummary] = useState<{ rules: number; txns: number } | null>(null);
  const [bulkCategoryId, setBulkCategoryId] = useState('');

  const loadData = useCallback(async () => {
    if (!companyId || transactionIds.length === 0) return;
    setLoading(true);
    try {
      const batchSize = 200;
      const allTxns: ReviewTransaction[] = [];
      for (let i = 0; i < transactionIds.length; i += batchSize) {
        const chunk = transactionIds.slice(i, i + batchSize);
        const { data } = await supabase
          .from('bank_transactions')
          .select('id, description, amount, transaction_date, accounting_category_id, bank_accounts(nickname), accounting_categories(name)')
          .in('id', chunk)
          .order('transaction_date', { ascending: false });
        if (data) {
          for (const row of data) {
            allTxns.push({
              ...row,
              bank_accounts: Array.isArray(row.bank_accounts) ? row.bank_accounts[0] ?? null : row.bank_accounts,
              accounting_categories: Array.isArray(row.accounting_categories) ? row.accounting_categories[0] ?? null : row.accounting_categories,
            } as ReviewTransaction);
          }
        }
      }
      setTransactions(allTxns);

      const { data: cats } = await supabase
        .from('accounting_categories')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      setCategories(cats ?? []);
    } finally {
      setLoading(false);
    }
  }, [companyId, transactionIds]);

  useEffect(() => {
    if (isOpen) {
      setEdits(new Map());
      setSavedSummary(null);
      setBulkCategoryId('');
      loadData();
    }
  }, [isOpen, loadData]);

  const getCategoryForTxn = useCallback(
    (txn: ReviewTransaction): string | null => {
      return edits.get(txn.id) ?? txn.accounting_category_id;
    },
    [edits],
  );

  const unclassifiedIds = useMemo(() => {
    return transactions
      .filter((t) => !getCategoryForTxn(t))
      .map((t) => t.id);
  }, [transactions, getCategoryForTxn]);

  const stats = useMemo(() => {
    const total = transactions.length;
    const classified = transactions.filter((t) => getCategoryForTxn(t)).length;
    return { total, classified, unclassified: total - classified };
  }, [transactions, getCategoryForTxn]);

  const handleCategoryChange = useCallback((txnId: string, categoryId: string) => {
    setEdits((prev) => {
      const next = new Map(prev);
      if (categoryId) {
        next.set(txnId, categoryId);
      } else {
        next.delete(txnId);
      }
      return next;
    });
  }, []);

  const handleBulkAssign = useCallback(() => {
    if (!bulkCategoryId) return;
    setEdits((prev) => {
      const next = new Map(prev);
      for (const id of unclassifiedIds) {
        next.set(id, bulkCategoryId);
      }
      return next;
    });
  }, [bulkCategoryId, unclassifiedIds]);

  const handleSave = useCallback(async () => {
    if (edits.size === 0) {
      onComplete();
      onClose();
      return;
    }
    setSaving(true);
    try {
      const editEntries = Array.from(edits.entries());
      for (const [txnId, catId] of editEntries) {
        await supabase
          .from('bank_transactions')
          .update({ accounting_category_id: catId })
          .eq('id', txnId);
      }

      let rulesCreated = 0;
      const txnMap = new Map(transactions.map((t) => [t.id, t]));
      const seenDescriptions = new Set<string>();

      for (const [txnId, catId] of editEntries) {
        const txn = txnMap.get(txnId);
        if (!txn) continue;
        const desc = txn.description.trim();
        if (!desc || seenDescriptions.has(desc.toLowerCase())) continue;
        seenDescriptions.add(desc.toLowerCase());

        const { data: existing } = await supabase
          .from('bank_category_rules')
          .select('id, category_id')
          .eq('company_id', companyId)
          .eq('exact_description', desc)
          .maybeSingle();

        if (existing) {
          if (existing.category_id !== catId) {
            await supabase
              .from('bank_category_rules')
              .update({ category_id: catId })
              .eq('id', existing.id);
            rulesCreated++;
          }
        } else {
          const result = await bankCategoryRulesService.createRule({
            company_id: companyId,
            exact_description: desc,
            category_id: catId,
          });
          if (!result.error) rulesCreated++;
        }
      }

      setSavedSummary({ rules: rulesCreated, txns: editEntries.length });
    } finally {
      setSaving(false);
    }
  }, [edits, transactions, companyId, onComplete, onClose]);

  const handleDone = useCallback(() => {
    onComplete();
    onClose();
  }, [onComplete, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50" dir="rtl">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-50 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-teal-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">סקירת סיווג עסקאות</h2>
              <p className="text-sm text-gray-500">
                {stats.total} עסקאות יובאו
                {stats.unclassified > 0
                  ? ` \u00B7 ${stats.unclassified} טרם סווגו`
                  : ' \u00B7 כולן סווגו'}
              </p>
            </div>
          </div>
          <button
            onClick={savedSummary ? handleDone : onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Saved summary */}
        {savedSummary && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-green-800">
                {savedSummary.txns} עסקאות עודכנו בהצלחה
              </p>
              {savedSummary.rules > 0 && (
                <p className="text-sm text-green-700 mt-1">
                  {savedSummary.rules} כללי סיווג חדשים נשמרו. בפעם הבאה העסקאות האלה יסווגו אוטומטית.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Bulk assign bar */}
        {!savedSummary && unclassifiedIds.length > 0 && (
          <div className="mx-6 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3 flex-wrap">
            <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
            <span className="text-sm text-amber-800 font-medium">
              {unclassifiedIds.length} עסקאות ללא סיווג
            </span>
            <div className="flex items-center gap-2 mr-auto">
              <select
                value={bulkCategoryId}
                onChange={(e) => setBulkCategoryId(e.target.value)}
                className="text-sm border border-amber-300 rounded-md px-2 py-1 bg-white focus:ring-2 focus:ring-amber-400 focus:border-transparent"
              >
                <option value="">בחר קטגוריה...</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <button
                onClick={handleBulkAssign}
                disabled={!bulkCategoryId}
                className="text-sm font-medium text-amber-700 hover:text-amber-900 disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded hover:bg-amber-100 transition-colors"
              >
                החל על כולן
              </button>
            </div>
          </div>
        )}

        {/* Transaction list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-teal-600" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              אין עסקאות לסקירה
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-right pb-2 font-medium text-gray-500 w-24">תאריך</th>
                  <th className="text-right pb-2 font-medium text-gray-500">תיאור</th>
                  <th className="text-right pb-2 font-medium text-gray-500 w-28">סכום</th>
                  <th className="text-right pb-2 font-medium text-gray-500 w-48">קטגוריה</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((txn) => {
                  const currentCat = getCategoryForTxn(txn);
                  const isEdited = edits.has(txn.id);
                  const isUnclassified = !currentCat;

                  return (
                    <tr
                      key={txn.id}
                      className={`transition-colors ${
                        isUnclassified
                          ? 'bg-amber-50/60'
                          : isEdited
                            ? 'bg-teal-50/40'
                            : ''
                      }`}
                    >
                      <td className="py-2.5 text-gray-600 whitespace-nowrap">
                        {fmtDate(txn.transaction_date)}
                      </td>
                      <td className="py-2.5 text-gray-900 font-medium">
                        <div className="flex items-center gap-2">
                          {txn.description}
                          {isEdited && (
                            <span className="inline-flex items-center gap-1 text-xs text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded">
                              <Sparkles className="h-3 w-3" />
                              עודכן
                            </span>
                          )}
                        </div>
                        {txn.bank_accounts?.nickname && (
                          <span className="text-xs text-gray-400">{txn.bank_accounts.nickname}</span>
                        )}
                      </td>
                      <td className={`py-2.5 whitespace-nowrap font-medium tabular-nums ${txn.amount >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {fmtAmount(txn.amount)}
                      </td>
                      <td className="py-2.5">
                        <div className="relative">
                          <select
                            value={currentCat ?? ''}
                            onChange={(e) => handleCategoryChange(txn.id, e.target.value)}
                            disabled={!!savedSummary}
                            className={`w-full text-sm rounded-md px-2 py-1.5 appearance-none pr-7 transition-colors ${
                              isUnclassified
                                ? 'border-amber-300 bg-amber-50 text-amber-800 font-medium focus:ring-amber-400'
                                : 'border-gray-200 bg-white text-gray-700 focus:ring-teal-400'
                            } border focus:ring-2 focus:border-transparent disabled:opacity-60`}
                          >
                            <option value="">-- לא מסווג --</option>
                            {categories.map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                          <ChevronDown className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          {savedSummary ? (
            <>
              <div />
              <button
                onClick={handleDone}
                className="px-5 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors flex items-center gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                סיום
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
              >
                דלג
              </button>
              <div className="flex items-center gap-3">
                {edits.size > 0 && (
                  <span className="text-sm text-gray-500">{edits.size} שינויים</span>
                )}
                <button
                  onClick={handleSave}
                  disabled={saving || edits.size === 0}
                  className="px-5 py-2.5 bg-teal-600 text-white rounded-lg font-medium hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  שמור ולמד
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
